//frontend/src/components/delivery/Profile.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
    User, 
    Mail, 
    MapPin, 
    Calendar, 
    Phone,
    Loader2,
    Shield,
    Clock,
    Building,
    CheckCircle2,
    XCircle,
    ChevronRight
} from 'lucide-react';
import axios from 'axios';

const ProfileSection = ({ title, children }) => (
    <div className="space-y-3 sm:space-y-5">
        <h3 className="text-base font-semibold text-gray-900 font-['Crimson_Text']">
            {title}
        </h3>
        {children}
    </div>
);

const ProfileItem = ({ icon, label, value, href }) => (
    <div className="flex items-center gap-2 sm:gap-3">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#dc2626]/10 flex items-center justify-center flex-shrink-0">
            {icon}
        </div>
        <div className="overflow-hidden">
            <p className="text-xs text-gray-500">{label}</p>
            {href ? (
                <a href={href} className="font-medium text-sm text-[#dc2626] hover:text-[#dc2626]/80 transition-colors truncate block">
                    {value}
                </a>
            ) : (
                <p className="font-medium text-sm text-gray-900 truncate">{value}</p>
            )}
        </div>
    </div>
);

const AreaItem = ({ pincode, areaName }) => (
    <div className="flex items-center p-2 sm:p-3 bg-white rounded-lg border border-gray-200 hover:border-[#dc2626]/20 transition-colors">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#dc2626]/10 flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
            <MapPin className="w-4 h-4 text-[#dc2626]" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="font-medium text-xs sm:text-sm text-gray-900 truncate">{areaName}</p>
            <p className="text-xs text-gray-500">{pincode}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
    </div>
);

const DeliveryProfile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('deliveryStaffInfo') 
                ? JSON.parse(localStorage.getItem('deliveryStaffInfo')).token 
                : null;

            if (!token) {
                toast.error('Authentication required');
                return;
            }

            const { data } = await axios.get('/api/v1/delivery/profile', {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                withCredentials: true
            });

            if (data.success) {
                setProfile(data.deliveryStaff);
            }
        } catch (error) {
            console.error('Profile fetch error:', error);
            toast.error(error.response?.data?.message || 'Error fetching profile');
        } finally {
            setLoading(false);
        }
    };

    const getAreaName = (pincode) => {
        const areas = {
            '562130': 'Gollarahatti',
            '562130': 'Kadabagere',
            '560112': 'Chikka Gollarahatti',
            '560091': 'Machohalli',
            '560091': 'Anjana Nagar',
            '560091': 'Byadarahalli',
            '562130': 'Tavarekere',
            '560091': 'Herohalli',
            '560091': 'Viswaneedam',
            '560091': 'Sunkadakatte',
            '560091': 'Muddayanapalya',
            '560091': 'Annapurneshwari Nagar',
            '560091': 'Kottigepalya',
            '560091': 'Sumanahalli'
        };
        return areas[pincode] || pincode;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 animate-spin text-[#dc2626]" />
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="text-center py-8 sm:py-10">
                <User className="w-12 h-12 sm:w-14 sm:h-14 mx-auto text-gray-400 mb-3" />
                <h3 className="text-base font-medium text-gray-900 mb-2">Profile Not Found</h3>
                <p className="text-sm text-gray-500">Unable to load profile information.</p>
            </div>
        );
    }

    return (
        <div 
            className="min-h-screen py-4 sm:py-6"
            style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundRepeat: 'repeat',
                backgroundSize: 'auto',
                backgroundPosition: 'center'
            }}
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    {/* Profile Header */}
                    <div 
                        className="px-4 py-5 sm:px-6 sm:py-10"
                        style={{
                            background: 'linear-gradient(to right, #dc2626, #ef4444)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    >
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
                            <div className="relative">
                                <div className="w-16 h-16 sm:w-24 md:w-28 sm:h-24 md:h-28 rounded-full overflow-hidden border-4 border-white shadow-lg">
                                    {profile.profilePicture ? (
                                        <img
                                            src={profile.profilePicture}
                                            alt={profile.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-white flex items-center justify-center">
                                            <User className="w-8 h-8 sm:w-10 sm:h-10 text-[#dc2626]" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="text-center sm:text-left">
                                <h1 className="text-xl sm:text-2xl font-bold text-white font-['Crimson_Text']">
                                    {profile.name}
                                </h1>
                                <p className="text-red-100 mt-1 flex items-center justify-center sm:justify-start text-sm">
                                    <Shield className="w-3 h-3 mr-1" />
                                    Delivery Staff
                                </p>
                                <div className="mt-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        profile.isBlocked 
                                            ? 'bg-red-100 text-red-800' 
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {profile.isBlocked ? (
                                            <XCircle className="w-3 h-3 mr-1" />
                                        ) : (
                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                        )}
                                        {profile.isBlocked ? 'Blocked' : 'Active'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-4 sm:p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                            {/* Personal Information */}
                            <ProfileSection title="Personal Information">
                                <div className="bg-gray-50 rounded-xl p-4 sm:p-5 space-y-3 sm:space-y-4">
                                    <ProfileItem 
                                        icon={<User className="w-4 h-4 text-[#dc2626]" />}
                                        label="Login ID"
                                        value={profile.loginId}
                                    />

                                    {profile.email && (
                                        <ProfileItem 
                                            icon={<Mail className="w-4 h-4 text-[#dc2626]" />}
                                            label="Email Address"
                                            value={profile.email}
                                        />
                                    )}

                                    <ProfileItem 
                                        icon={<Phone className="w-4 h-4 text-[#dc2626]" />}
                                        label="Contact Number"
                                        value={profile.phone ? profile.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : "Not provided"}
                                        href={profile.phone ? `tel:${profile.phone}` : null}
                                    />

                                    <ProfileItem 
                                        icon={<Calendar className="w-4 h-4 text-[#dc2626]" />}
                                        label="Age"
                                        value={`${profile.age} Years`}
                                    />
                                </div>
                            </ProfileSection>

                            {/* Assigned Areas */}
                            <ProfileSection title="Assigned Areas">
                                <div className="bg-gray-50 rounded-xl p-4 sm:p-5">
                                    <div className="grid gap-2 sm:gap-3">
                                        {profile.assignedPincodes?.length > 0 ? (
                                            profile.assignedPincodes.map(pincode => (
                                                <AreaItem 
                                                    key={pincode}
                                                    pincode={pincode}
                                                    areaName={getAreaName(pincode)}
                                                />
                                            ))
                                        ) : (
                                            <div className="text-center py-4">
                                                <p className="text-sm text-gray-500">No areas assigned yet</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </ProfileSection>
                        </div>

                        {/* Additional Information */}
                        <div className="mt-5 pt-5 sm:mt-6 sm:pt-6 border-t border-gray-200">
                            <ProfileSection title="Additional Information">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                    <div className="bg-gray-50 rounded-xl p-4 sm:p-5">
                                        <ProfileItem 
                                            icon={<Building className="w-4 h-4 text-[#dc2626]" />}
                                            label="Residential Address"
                                            value={profile.address || "Not provided"}
                                        />
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-4 sm:p-5">
                                        <ProfileItem 
                                            icon={<Clock className="w-4 h-4 text-[#dc2626]" />}
                                            label="Joined On"
                                            value={new Date(profile.createdAt).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        />
                                    </div>
                                </div>
                            </ProfileSection>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeliveryProfile;